/**
 * Plot Feature Creation and Visualization
 *
 * This module creates the actual GeoJSON features that get rendered on the map.
 * It handles treatment parsing, color generation, plot subdivision, and buffer creation
 * to build the visual representation of agricultural test plots.
 *
 * Key responsibilities:
 * - Parse and deduplicate treatment rates from user input
 * - Generate color schemes for different treatments
 * - Create replicate features (rows of treatment plots)
 * - Subdivide treatment areas into individual plots
 * - Create plot buffer visualizations (blue hatched areas)
 * - Handle randomization and shuffling of treatments
 * - Generate field buffer features (gray hatched areas)
 *
 * Used by: visualizationBuilder.ts, TestAreasVisualizer.tsx (for legend)
 */

import * as turf from '@turf/turf';
import { Feature, Polygon, MultiPolygon, Position } from 'geojson';
import { getRotatedPlotCorners } from './plotCalculations';
import { convertPlotDimensions } from './coordinateConversions';

// =============================================================================
// GEOMETRY VALIDATION UTILITIES
// =============================================================================

/**
 * Validates that all corners of a polygon are within the usable area
 */
function validatePolygonCorners(
  corners: Position[],
  usableArea: Feature<Polygon | MultiPolygon>,
): boolean {
  return corners.every((corner) => {
    try {
      return turf.booleanPointInPolygon(turf.point(corner), usableArea);
    } catch (err) {
      return false;
    }
  });
}

/**
 * Performs comprehensive validation that entire polygon is within usable area
 * Uses grid sampling to check for intersections with holes or invalid areas
 */
function validatePolygonFully(
  polygon: Feature<Polygon>,
  usableArea: Feature<Polygon | MultiPolygon>,
  sampleSteps: number = 10,
): boolean {
  try {
    const plotBounds = turf.bbox(polygon);
    const [minLng, minLat, maxLng, maxLat] = plotBounds;

    for (let i = 0; i <= sampleSteps; i++) {
      for (let j = 0; j <= sampleSteps; j++) {
        const sampleLng = minLng + (i / sampleSteps) * (maxLng - minLng);
        const sampleLat = minLat + (j / sampleSteps) * (maxLat - minLat);
        const samplePoint = turf.point([sampleLng, sampleLat]);

        // Only check points that are actually inside the plot
        if (turf.booleanPointInPolygon(samplePoint, polygon)) {
          if (!turf.booleanPointInPolygon(samplePoint, usableArea)) {
            return false;
          }
        }
      }
    }
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * Complete validation that checks both corners and full polygon coverage
 */
function validatePlotPosition(
  corners: Position[],
  usableArea: Feature<Polygon | MultiPolygon>,
  sampleSteps: number = 10,
): boolean {
  // Quick corner check first
  if (!validatePolygonCorners(corners, usableArea)) {
    return false;
  }

  // Full polygon validation
  const plotPolygon = turf.polygon([[...corners, corners[0]]]);
  return validatePolygonFully(plotPolygon, usableArea, sampleSteps);
}

// =============================================================================
// COORDINATE AND SEARCH UTILITIES
// =============================================================================

/**
 * Subdivides a replicate boundary to create treatment corners
 */
function subdivideReplicateBoundary(
  cleanCorners: Position[],
  startRatio: number,
  endRatio: number,
): Position[] {
  const [
    replicateBottomLeft,
    replicateBottomRight,
    replicateTopRight,
    replicateTopLeft,
  ] = cleanCorners;

  // Treatments are arranged along the WIDTH direction (perpendicular to AB line)
  const treatmentBottomLeft = lerp(
    replicateBottomLeft,
    replicateBottomRight,
    startRatio,
  );
  const treatmentBottomRight = lerp(
    replicateBottomLeft,
    replicateBottomRight,
    endRatio,
  );
  const treatmentTopRight = lerp(replicateTopLeft, replicateTopRight, endRatio);
  const treatmentTopLeft = lerp(
    replicateTopLeft,
    replicateTopRight,
    startRatio,
  );

  return [
    treatmentBottomLeft,
    treatmentBottomRight,
    treatmentTopRight,
    treatmentTopLeft,
    treatmentBottomLeft,
  ];
}

// =============================================================================
// AB LINE ANALYSIS UTILITIES
// =============================================================================

interface ABLineSegment {
  start: Position;
  end: Position;
  length: number;
}

/**
 * Finds valid segments of AB line that are within the usable area
 */
function findValidABLineSegments(
  ablineGeoJSON: any,
  usableArea: Feature<Polygon | MultiPolygon>,
  replicateWidthDeg: number,
  replicateLengthDeg: number,
): ABLineSegment[] {
  if (!ablineGeoJSON?.features?.[0]?.geometry?.coordinates) {
    return [];
  }

  const ablineCoords = ablineGeoJSON.features[0].geometry.coordinates;

  // Validate that we have at least 2 coordinates for a valid line
  if (!Array.isArray(ablineCoords) || ablineCoords.length < 2) {
    return [];
  }

  try {
    const abline = turf.lineString(ablineCoords);
    const minSegmentLength =
      Math.max(replicateWidthDeg, replicateLengthDeg) * 1.1;
    const lineLength = turf.length(abline, { units: 'kilometers' });
    const numTestPoints = Math.min(
      Math.max(20, Math.ceil(lineLength * 10)),
      100, // Performance constraint
    );

    return extractValidSegments(
      abline,
      usableArea,
      numTestPoints,
      minSegmentLength,
    );
  } catch (err) {
    return [];
  }
}

/**
 * Extracts continuous valid segments from an AB line within usable area
 */
function extractValidSegments(
  abline: Feature,
  usableArea: Feature<Polygon | MultiPolygon>,
  numTestPoints: number,
  minSegmentLength: number,
): ABLineSegment[] {
  const validSegments: ABLineSegment[] = [];
  const lineLength = turf.length(abline as any, { units: 'kilometers' });

  let currentSegmentStart: Position | null = null;
  let lastValidPoint: Position | null = null;

  for (let i = 0; i <= numTestPoints; i++) {
    const distanceAlong = (i / numTestPoints) * lineLength;
    const testPoint = turf.along(abline as any, distanceAlong, {
      units: 'kilometers',
    });
    const testCoord = testPoint.geometry.coordinates;

    const isInsideUsable = turf.booleanPointInPolygon(testPoint, usableArea);

    if (isInsideUsable) {
      if (currentSegmentStart === null) {
        currentSegmentStart = testCoord;
      }
      lastValidPoint = testCoord;
    } else {
      // End of valid segment
      const segment = createSegmentIfValid(
        currentSegmentStart,
        lastValidPoint,
        minSegmentLength,
      );
      if (segment) {
        validSegments.push(segment);
      }

      currentSegmentStart = null;
      lastValidPoint = null;
    }
  }

  // Check for final segment
  const finalSegment = createSegmentIfValid(
    currentSegmentStart,
    lastValidPoint,
    minSegmentLength,
  );
  if (finalSegment) {
    validSegments.push(finalSegment);
  }

  // Sort by length (longest first - prefer longer segments)
  return validSegments.sort((a, b) => b.length - a.length);
}

/**
 * Creates a valid segment if the conditions are met
 */
function createSegmentIfValid(
  start: Position | null,
  end: Position | null,
  minLength: number,
): ABLineSegment | null {
  if (!start || !end) return null;

  const segmentLength = turf.distance(turf.point(start), turf.point(end), {
    units: 'degrees',
  });

  if (segmentLength >= minLength) {
    return { start, end, length: segmentLength };
  }

  return null;
}

// Helper to parse and deduplicate treatments, sorted with nominal/lowest first
export function parseUniqueTreatments(
  treatmentRates: string,
  nominalRate: number,
): number[] {
  if (!treatmentRates) return [];

  const rates = treatmentRates
    .split(',')
    .map((r) => Number(r.trim()))
    .filter((r) => !isNaN(r));
  const uniqueRates = new Set(rates);

  // Add nominal rate if it's different from all treatment rates
  if (!uniqueRates.has(nominalRate)) {
    uniqueRates.add(nominalRate);
  }

  const sortedRates = Array.from(uniqueRates).sort((a, b) => a - b);

  // Ensure nominal rate comes first (gets darkest green)
  if (sortedRates.includes(nominalRate)) {
    const filtered = sortedRates.filter((rate) => rate !== nominalRate);
    return [nominalRate, ...filtered];
  }

  return sortedRates;
}

// Shuffle array in place (Fisher-Yates algorithm)
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Summer-like colormap: bright yellow (lowest) to dark green (highest)
export function generateSummerColors(count: number): string[] {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    const ratio = count === 1 ? 0 : i / Math.max(1, count - 1);
    // Summer colormap: bright yellow to dark green
    // Start: bright yellow (254, 253, 110), End: dark green (8, 121, 93)
    const r = Math.round(254 - ratio * (254 - 8)); // 254 to 8
    const g = Math.round(253 - ratio * (253 - 121)); // 253 to 121
    const b = Math.round(110 - ratio * (110 - 93)); // 110 to 93
    colors.push(`rgb(${r}, ${g}, ${b})`);
  }
  return colors;
}

// =============================================================================
// BUFFER CREATION UTILITIES
// =============================================================================

interface PlotCorners {
  bottomLeft: Position;
  bottomRight: Position;
  topRight: Position;
  topLeft: Position;
}

interface CreatePlotBufferParams {
  treatmentCorners: number[][];
  plotWidthFt: number;
  plotLengthFt: number;
  plotBufferWidthFt: number;
  plotBufferLengthFt: number;
}

/**
 * Calculates linear interpolation between two points
 */
function lerp(start: Position, end: Position, ratio: number): Position {
  return [
    start[0] + ratio * (end[0] - start[0]),
    start[1] + ratio * (end[1] - start[1]),
  ];
}

/**
 * Parses treatment corners into a structured format
 */
function parseCorners(treatmentCorners: number[][]): PlotCorners {
  const cleanCorners = treatmentCorners.slice(0, -1);
  const [bottomLeft, bottomRight, topRight, topLeft] = cleanCorners;

  return {
    bottomLeft: bottomLeft as Position,
    bottomRight: bottomRight as Position,
    topRight: topRight as Position,
    topLeft: topLeft as Position,
  };
}

/**
 * Creates a buffer polygon for a specific edge
 */
function createEdgeBuffer(
  corners: Position[],
  bufferType: 'plotBuffer',
): Feature<Polygon> {
  const closedCorners = [...corners, corners[0]];
  const buffer = turf.polygon([closedCorners]);
  buffer.properties = { type: bufferType };
  return buffer;
}

/**
 * Creates width buffers (left and right edges)
 */
function createWidthBuffers(
  corners: PlotCorners,
  bufferRatio: number,
): Feature<Polygon>[] {
  const buffers: Feature<Polygon>[] = [];

  // Left width buffer
  const leftCorners = [
    corners.bottomLeft,
    lerp(corners.bottomLeft, corners.bottomRight, bufferRatio),
    lerp(corners.topLeft, corners.topRight, bufferRatio),
    corners.topLeft,
  ];
  buffers.push(createEdgeBuffer(leftCorners, 'plotBuffer'));

  // Right width buffer
  const rightCorners = [
    lerp(corners.bottomRight, corners.bottomLeft, bufferRatio),
    corners.bottomRight,
    corners.topRight,
    lerp(corners.topRight, corners.topLeft, bufferRatio),
  ];
  buffers.push(createEdgeBuffer(rightCorners, 'plotBuffer'));

  return buffers;
}

/**
 * Creates length buffers (top and bottom edges)
 */
function createLengthBuffers(
  corners: PlotCorners,
  bufferRatio: number,
): Feature<Polygon>[] {
  const buffers: Feature<Polygon>[] = [];

  // Top length buffer
  const topCorners = [
    lerp(corners.topLeft, corners.bottomLeft, bufferRatio),
    lerp(corners.topRight, corners.bottomRight, bufferRatio),
    corners.topRight,
    corners.topLeft,
  ];
  buffers.push(createEdgeBuffer(topCorners, 'plotBuffer'));

  // Bottom length buffer
  const bottomCorners = [
    corners.bottomLeft,
    corners.bottomRight,
    lerp(corners.bottomRight, corners.topRight, bufferRatio),
    lerp(corners.bottomLeft, corners.topLeft, bufferRatio),
  ];
  buffers.push(createEdgeBuffer(bottomCorners, 'plotBuffer'));

  return buffers;
}

// Create plot buffers for a treatment area
export function createPlotBuffers({
  treatmentCorners,
  plotWidthFt,
  plotLengthFt,
  plotBufferWidthFt,
  plotBufferLengthFt,
}: CreatePlotBufferParams): Feature<Polygon>[] {
  try {
    const corners = parseCorners(treatmentCorners);
    const bufferFeatures: Feature<Polygon>[] = [];

    // Create width buffers if specified
    if (plotBufferWidthFt > 0) {
      const widthBufferRatio = plotBufferWidthFt / plotWidthFt;
      bufferFeatures.push(...createWidthBuffers(corners, widthBufferRatio));
    }

    // Create length buffers if specified
    if (plotBufferLengthFt > 0) {
      const lengthBufferRatio = plotBufferLengthFt / plotLengthFt;
      bufferFeatures.push(...createLengthBuffers(corners, lengthBufferRatio));
    }

    return bufferFeatures;
  } catch (err) {
    return [];
  }
}

// =============================================================================
// POSITIONING STRATEGY UTILITIES
// =============================================================================

export interface PositionSearchConfig {
  usableArea: Feature<Polygon | MultiPolygon>;
  replicateWidthDeg: number;
  replicateLengthDeg: number;
  ablineBearing?: number | null;
  ablineGeoJSON?: any;
}

export interface PositionResult {
  centerLng: number;
  centerLat: number;
  fitsInField: boolean;
}

/**
 * Strategy: Position without AB line constraints (simple center placement)
 */
export function positionWithoutABLine(
  config: PositionSearchConfig,
): PositionResult {
  const { usableArea, replicateWidthDeg, replicateLengthDeg } = config;

  const centerPoint = turf.center(usableArea);
  const [baseLng, baseLat] = centerPoint.geometry.coordinates;

  const testCorners = [
    [baseLng - replicateWidthDeg / 2, baseLat + replicateLengthDeg / 2],
    [baseLng + replicateWidthDeg / 2, baseLat + replicateLengthDeg / 2],
    [baseLng + replicateWidthDeg / 2, baseLat - replicateLengthDeg / 2],
    [baseLng - replicateWidthDeg / 2, baseLat - replicateLengthDeg / 2],
  ];

  // Validate that the entire replicate fits within the usable area
  const fitsInField = validatePlotPosition(testCorners, usableArea);

  return { centerLng: baseLng, centerLat: baseLat, fitsInField };
}

/**
 * Strategy: Position along valid AB line segments
 */
function positionAlongABSegments(
  config: PositionSearchConfig,
): PositionResult | null {
  const {
    usableArea,
    replicateWidthDeg,
    replicateLengthDeg,
    ablineBearing,
    ablineGeoJSON,
  } = config;

  const validABSegments = findValidABLineSegments(
    ablineGeoJSON,
    usableArea,
    replicateWidthDeg,
    replicateLengthDeg,
  );

  if (validABSegments.length === 0) {
    return null;
  }

  const bearingRad = (ablineBearing! * Math.PI) / 180;
  const deltaLng = Math.sin(bearingRad);
  const deltaLat = Math.cos(bearingRad);

  for (const segment of validABSegments) {
    const result = searchAlongSegment(segment, config, deltaLng, deltaLat);
    if (result) return result;
  }

  return null;
}

/**
 * Strategy: Position near AB line when it doesn't intersect usable area
 */
export function positionNearABLine(
  config: PositionSearchConfig,
): PositionResult | null {
  const {
    usableArea,
    replicateWidthDeg,
    replicateLengthDeg,
    ablineBearing,
    ablineGeoJSON,
  } = config;

  if (!ablineGeoJSON?.features?.[0]) {
    return null;
  }

  const ablineCoords = ablineGeoJSON.features[0].geometry.coordinates;

  // Validate that we have at least 2 coordinates for a valid line
  if (!Array.isArray(ablineCoords) || ablineCoords.length < 2) {
    return null;
  }

  const abline = turf.lineString(ablineCoords);

  // Search within usable area bounds starting from points closest to the AB line
  const usableAreaBounds = turf.bbox(usableArea);
  const [minLng, minLat, maxLng, maxLat] = usableAreaBounds;

  // Create grid search within usable area, prioritizing points closer to AB line
  const gridSteps = 20;
  const candidates: Array<{ pos: Position; distanceToAB: number }> = [];

  for (let i = 0; i < gridSteps; i++) {
    for (let j = 0; j < gridSteps; j++) {
      const testLng = minLng + (i / (gridSteps - 1)) * (maxLng - minLng);
      const testLat = minLat + (j / (gridSteps - 1)) * (maxLat - minLat);
      const testPoint = turf.point([testLng, testLat]);

      // Only consider points within usable area
      if (turf.booleanPointInPolygon(testPoint, usableArea)) {
        const distanceToAB = turf.pointToLineDistance(testPoint, abline, {
          units: 'degrees',
        });
        candidates.push({ pos: [testLng, testLat], distanceToAB });
      }
    }
  }

  // Sort by distance to AB line (closest first)
  candidates.sort((a, b) => a.distanceToAB - b.distanceToAB);

  // Test candidates starting with those closest to AB line
  for (let i = 0; i < Math.min(candidates.length, 50); i++) {
    const [testLng, testLat] = candidates[i].pos;

    const testCorners = getRotatedPlotCorners(
      testLng,
      testLat,
      replicateWidthDeg,
      replicateLengthDeg,
      ablineBearing!,
    );

    if (validatePlotPosition(testCorners, usableArea, 8)) {
      return { centerLng: testLng, centerLat: testLat, fitsInField: true };
    }
  }

  return null;
}

/**
 * Strategy: General AB line search (fallback for complex cases)
 */
function positionGeneralABSearch(config: PositionSearchConfig): PositionResult {
  const {
    usableArea,
    replicateWidthDeg,
    replicateLengthDeg,
    ablineBearing,
    ablineGeoJSON,
  } = config;

  // Get baseline position - prefer AB line intersection center if AB line crosses usable area
  let baseLng, baseLat;

  if (ablineGeoJSON?.features?.[0]?.geometry?.coordinates) {
    const ablineCoords = ablineGeoJSON.features[0].geometry.coordinates;

    if (Array.isArray(ablineCoords) && ablineCoords.length >= 2) {
      try {
        const abline = turf.lineString(ablineCoords);

        // Check if AB line intersects with the usable area
        const intersection = turf.lineIntersect(abline, usableArea);

        if (intersection.features.length >= 2) {
          // AB line crosses usable area - use center of the intersecting segment
          const firstIntersection =
            intersection.features[0].geometry.coordinates;
          const lastIntersection =
            intersection.features[intersection.features.length - 1].geometry
              .coordinates;

          baseLng = (firstIntersection[0] + lastIntersection[0]) / 2;
          baseLat = (firstIntersection[1] + lastIntersection[1]) / 2;
        } else {
          // AB line doesn't cross usable area meaningfully - use usable area center
          const centerPoint = turf.center(usableArea);
          [baseLng, baseLat] = centerPoint.geometry.coordinates;
        }
      } catch (err) {
        // Error in intersection calculation - fall back to usable area center
        const centerPoint = turf.center(usableArea);
        [baseLng, baseLat] = centerPoint.geometry.coordinates;
      }
    } else {
      // Invalid AB line - fall back to usable area center
      const centerPoint = turf.center(usableArea);
      [baseLng, baseLat] = centerPoint.geometry.coordinates;
    }
  } else {
    // No AB line - use usable area center
    const centerPoint = turf.center(usableArea);
    [baseLng, baseLat] = centerPoint.geometry.coordinates;
  }

  const bounds = turf.bbox(usableArea);
  const [minLng, minLat, maxLng, maxLat] = bounds;

  const bearingRad = (ablineBearing! * Math.PI) / 180;
  const deltaLng = Math.sin(bearingRad);
  const deltaLat = Math.cos(bearingRad);

  // Search along AB line direction
  const searchDistance = Math.max(maxLng - minLng, maxLat - minLat);
  const stepSize = Math.max(
    Math.min(replicateWidthDeg, replicateLengthDeg) / 4,
    0.0001,
  );
  const maxSteps = Math.min(Math.ceil(searchDistance / stepSize), 100);

  // Test positions along the AB line
  for (let step = 0; step <= maxSteps; step++) {
    const distances = step === 0 ? [0] : [-step * stepSize, step * stepSize];

    for (const distance of distances) {
      const testLng = baseLng + distance * deltaLng;
      const testLat = baseLat + distance * deltaLat;

      if (
        testLng < minLng ||
        testLng > maxLng ||
        testLat < minLat ||
        testLat > maxLat
      ) {
        continue;
      }

      const testCorners = getRotatedPlotCorners(
        testLng,
        testLat,
        replicateWidthDeg,
        replicateLengthDeg,
        ablineBearing!,
      );

      if (validatePlotPosition(testCorners, usableArea)) {
        return { centerLng: testLng, centerLat: testLat, fitsInField: true };
      }
    }
  }

  // Try lateral shifts if direct AB line search fails
  const lateralResult = searchLateralShifts(
    config,
    baseLng,
    baseLat,
    bearingRad,
    deltaLng,
    deltaLat,
    maxSteps,
    stepSize,
    bounds,
  );
  if (lateralResult) return lateralResult;

  // Validate that the entire replicate fits at the baseline position
  const testCorners = [
    [baseLng - replicateWidthDeg / 2, baseLat + replicateLengthDeg / 2],
    [baseLng + replicateWidthDeg / 2, baseLat + replicateLengthDeg / 2],
    [baseLng + replicateWidthDeg / 2, baseLat - replicateLengthDeg / 2],
    [baseLng - replicateWidthDeg / 2, baseLat - replicateLengthDeg / 2],
  ];

  const fitsInField = validatePlotPosition(testCorners, usableArea);

  return { centerLng: baseLng, centerLat: baseLat, fitsInField };
}

/**
 * Helper function to search along a specific AB line segment
 */
function searchAlongSegment(
  segment: ABLineSegment,
  config: PositionSearchConfig,
  deltaLng: number,
  deltaLat: number,
): PositionResult | null {
  const { usableArea, replicateWidthDeg, replicateLengthDeg, ablineBearing } =
    config;

  const segmentCenter = turf.center(
    turf.lineString([segment.start, segment.end]),
  );
  const [segLng, segLat] = segmentCenter.geometry.coordinates;

  const segmentLength = turf.distance(
    turf.point(segment.start),
    turf.point(segment.end),
    { units: 'kilometers' },
  );

  const segmentSearchSteps = Math.ceil(
    (segmentLength * 1000) /
      ((Math.min(replicateWidthDeg, replicateLengthDeg) * 111000) / 4),
  );
  const segmentStepSize = segmentLength / segmentSearchSteps / 111;

  for (let step = 0; step <= segmentSearchSteps; step++) {
    const distances =
      step === 0 ? [0] : [-step * segmentStepSize, step * segmentStepSize];

    for (const distance of distances) {
      const testLng = segLng + distance * deltaLng;
      const testLat = segLat + distance * deltaLat;

      // Check if still within segment bounds
      const testPoint = turf.point([testLng, testLat]);
      const segmentLine = turf.lineString([segment.start, segment.end]);
      const nearestPoint = turf.nearestPointOnLine(segmentLine, testPoint);

      if (turf.distance(testPoint, nearestPoint, { units: 'meters' }) > 100) {
        continue;
      }

      const testCorners = getRotatedPlotCorners(
        testLng,
        testLat,
        replicateWidthDeg,
        replicateLengthDeg,
        ablineBearing!,
      );

      if (validatePlotPosition(testCorners, usableArea)) {
        return { centerLng: testLng, centerLat: testLat, fitsInField: true };
      }
    }
  }

  return null;
}

/**
 * Helper function to search with lateral shifts perpendicular to AB line
 */
function searchLateralShifts(
  config: PositionSearchConfig,
  baseLng: number,
  baseLat: number,
  bearingRad: number,
  deltaLng: number,
  deltaLat: number,
  maxSteps: number,
  stepSize: number,
  bounds: number[],
): PositionResult | null {
  const { usableArea, replicateWidthDeg, replicateLengthDeg, ablineBearing } =
    config;
  const [minLng, minLat, maxLng, maxLat] = bounds;

  const perpBearingRad = bearingRad + Math.PI / 2;
  const perpDeltaLng = Math.sin(perpBearingRad);
  const perpDeltaLat = Math.cos(perpBearingRad);

  const lateralSteps = Math.min(20, maxSteps / 5);
  const lateralStepSize = stepSize;

  for (let lateralStep = 1; lateralStep <= lateralSteps; lateralStep++) {
    const lateralDistances = [
      -lateralStep * lateralStepSize,
      lateralStep * lateralStepSize,
    ];

    for (const lateralDistance of lateralDistances) {
      const shiftedBaseLng = baseLng + lateralDistance * perpDeltaLng;
      const shiftedBaseLat = baseLat + lateralDistance * perpDeltaLat;

      for (let step = 0; step <= maxSteps; step++) {
        const distances =
          step === 0 ? [0] : [-step * stepSize, step * stepSize];

        for (const distance of distances) {
          const testLng = shiftedBaseLng + distance * deltaLng;
          const testLat = shiftedBaseLat + distance * deltaLat;

          if (
            testLng < minLng ||
            testLng > maxLng ||
            testLat < minLat ||
            testLat > maxLat
          ) {
            continue;
          }

          const testCorners = getRotatedPlotCorners(
            testLng,
            testLat,
            replicateWidthDeg,
            replicateLengthDeg,
            ablineBearing!,
          );

          if (validatePlotPosition(testCorners, usableArea)) {
            return {
              centerLng: testLng,
              centerLat: testLat,
              fitsInField: true,
            };
          }
        }
      }
    }
  }

  return null;
}

// Find optimal position for replicate along AB line to fit within usable area
export function findReplicatePosition(
  config: PositionSearchConfig,
): PositionResult {
  const { ablineBearing } = config;

  // Strategy 1: No AB line - simple center placement
  if (ablineBearing === null || ablineBearing === undefined) {
    return positionWithoutABLine(config);
  }

  // Strategy 2: Try positioning along valid AB line segments
  const segmentResult = positionAlongABSegments(config);
  if (segmentResult) {
    return segmentResult;
  }

  // Strategy 3: Position near AB line when it doesn't intersect usable area
  const nearResult = positionNearABLine(config);
  if (nearResult) {
    return nearResult;
  }

  // Strategy 4: General AB line search as fallback
  const generalResult = positionGeneralABSearch(config);
  if (generalResult.fitsInField) {
    return generalResult;
  }

  // Strategy 5: Final fallback - ignore AB line and try center of usable area
  // This handles cases where plot is too big to fit along AB line but might fit centered
  return positionWithoutABLine(config);
}

// =============================================================================
// FEATURE FACTORY UTILITIES
// =============================================================================

interface CreateReplicateParams {
  usableArea: Feature<Polygon | MultiPolygon>;
  treatmentRates: string;
  nominalRate: number;
  plotWidthFt: number;
  plotLengthFt: number;
  plotBufferWidthFt: number;
  plotBufferLengthFt: number;
  isPlot: boolean;
  ablineBearing?: number | null;
  ablineGeoJSON?: any;
  metersToDegreesLng: number;
  metersToDegreesLat: number;
}

interface ReplicateBoundaryInfo {
  boundary: Feature<Polygon> & { clippedBoundary?: Feature<Polygon> };
  renderBoundary: Feature<Polygon>;
  centerLng: number;
  centerLat: number;
  fitsInField: boolean;
}

/**
 * Creates the boundary for plot-based replicates
 */
function createPlotReplicateBoundary(
  centerLng: number,
  centerLat: number,
  replicateWidthDeg: number,
  replicateLengthDeg: number,
  ablineBearing?: number | null,
): Feature<Polygon> {
  if (ablineBearing !== null && ablineBearing !== undefined) {
    const replicateCorners = getRotatedPlotCorners(
      centerLng,
      centerLat,
      replicateWidthDeg,
      replicateLengthDeg,
      ablineBearing,
    );
    replicateCorners.push(replicateCorners[0]);
    return turf.polygon([replicateCorners]);
  } else {
    const replicateCorners = [
      [centerLng - replicateWidthDeg / 2, centerLat + replicateLengthDeg / 2],
      [centerLng + replicateWidthDeg / 2, centerLat + replicateLengthDeg / 2],
      [centerLng + replicateWidthDeg / 2, centerLat - replicateLengthDeg / 2],
      [centerLng - replicateWidthDeg / 2, centerLat - replicateLengthDeg / 2],
      [centerLng - replicateWidthDeg / 2, centerLat + replicateLengthDeg / 2],
    ];
    return turf.polygon([replicateCorners]);
  }
}

/**
 * Creates strip boundary for strip-based replicates
 */
function createStripReplicateBoundary(
  usableArea: Feature<Polygon | MultiPolygon>,
  centerLng: number,
  centerLat: number,
  replicateWidthDeg: number,
  ablineBearing?: number | null,
): Feature<Polygon> & { clippedBoundary?: Feature<Polygon> } {
  const bbox = turf.bbox(usableArea);
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const usableWidthDeg = maxLng - minLng;
  const usableLengthDeg = maxLat - minLat;

  let initialStripBoundary: Feature<Polygon>;

  if (ablineBearing !== null && ablineBearing !== undefined) {
    // Create strips aligned with AB line
    // Use the center of the usable area instead of the field center for better constraint handling
    const usableCenterLng = (bbox[0] + bbox[2]) / 2;
    const usableCenterLat = (bbox[1] + bbox[3]) / 2;
    // Use a more conservative strip length based on the actual usable area
    const stripLength = Math.max(usableWidthDeg, usableLengthDeg) * 1.5;

    const stripCorners = getRotatedPlotCorners(
      usableCenterLng,
      usableCenterLat,
      replicateWidthDeg,
      stripLength,
      ablineBearing,
    );
    stripCorners.push(stripCorners[0]);
    initialStripBoundary = turf.polygon([stripCorners]);
  } else {
    // Create strips without AB line alignment
    // Use conservative padding based on usable area dimensions
    const padding = Math.max(usableWidthDeg, usableLengthDeg) * 0.2;
    let stripBoundaryCorners: Position[];

    if (usableWidthDeg > usableLengthDeg) {
      // Horizontal strips
      stripBoundaryCorners = [
        [minLng - padding, centerLat - replicateWidthDeg / 2],
        [maxLng + padding, centerLat - replicateWidthDeg / 2],
        [maxLng + padding, centerLat + replicateWidthDeg / 2],
        [minLng - padding, centerLat + replicateWidthDeg / 2],
        [minLng - padding, centerLat - replicateWidthDeg / 2],
      ];
    } else {
      // Vertical strips
      stripBoundaryCorners = [
        [centerLng - replicateWidthDeg / 2, minLat - padding],
        [centerLng + replicateWidthDeg / 2, minLat - padding],
        [centerLng + replicateWidthDeg / 2, maxLat + padding],
        [centerLng - replicateWidthDeg / 2, maxLat + padding],
        [centerLng - replicateWidthDeg / 2, minLat - padding],
      ];
    }

    initialStripBoundary = turf.polygon([stripBoundaryCorners]);
  }

  // Clip to usable area
  const result = initialStripBoundary as Feature<Polygon> & {
    clippedBoundary?: Feature<Polygon>;
  };

  try {
    const featuresCollection = turf.featureCollection([
      initialStripBoundary,
      usableArea,
    ]);
    const intersection = turf.intersect(featuresCollection);

    if (intersection && intersection.geometry) {
      result.clippedBoundary = intersection as Feature<Polygon>;
    } else {
      // If intersection fails, use the usable area itself
      result.clippedBoundary = usableArea as Feature<Polygon>;
    }
  } catch (err) {
    // On any error, use the usable area
    result.clippedBoundary = usableArea as Feature<Polygon>;
  }

  return result;
}

/**
 * Determines positioning and creates replicate boundary
 */
function createReplicateBoundaryInfo(
  config: CreateReplicateParams,
  shuffledTreatments: number[],
  plotDimensions: { widthM: number; lengthM: number },
): ReplicateBoundaryInfo {
  const {
    usableArea,
    isPlot,
    ablineBearing,
    ablineGeoJSON,
    metersToDegreesLng,
    metersToDegreesLat,
  } = config;

  const plotWidthDeg = plotDimensions.widthM * metersToDegreesLng;
  const plotLengthDeg = plotDimensions.lengthM * metersToDegreesLat;
  const replicateWidthDeg = shuffledTreatments.length * plotWidthDeg;
  const replicateLengthDeg = plotLengthDeg;

  let centerLng: number, centerLat: number, fitsInField: boolean;

  if (isPlot) {
    // Use positioning logic for plots
    const positionResult = findReplicatePosition({
      usableArea,
      replicateWidthDeg,
      replicateLengthDeg,
      ablineBearing,
      ablineGeoJSON,
    });
    centerLng = positionResult.centerLng;
    centerLat = positionResult.centerLat;
    fitsInField = positionResult.fitsInField;

    if (!fitsInField) {
      // Return early - will result in empty features
      return {
        boundary: turf.polygon([
          [
            [0, 0],
            [0, 0],
            [0, 0],
            [0, 0],
          ],
        ]) as Feature<Polygon> & { clippedBoundary?: Feature<Polygon> },
        renderBoundary: turf.polygon([
          [
            [0, 0],
            [0, 0],
            [0, 0],
            [0, 0],
          ],
        ]),
        centerLng,
        centerLat,
        fitsInField: false,
      };
    }

    const boundary = createPlotReplicateBoundary(
      centerLng,
      centerLat,
      replicateWidthDeg,
      replicateLengthDeg,
      ablineBearing,
    ) as Feature<Polygon> & { clippedBoundary?: Feature<Polygon> };

    return {
      boundary,
      renderBoundary: boundary,
      centerLng,
      centerLat,
      fitsInField: true,
    };
  } else {
    // Strips positioning and boundary creation
    const bbox = turf.bbox(usableArea);
    centerLng = (bbox[0] + bbox[2]) / 2;
    centerLat = (bbox[1] + bbox[3]) / 2;
    fitsInField = true;

    const boundary = createStripReplicateBoundary(
      usableArea,
      centerLng,
      centerLat,
      replicateWidthDeg,
      ablineBearing,
    );
    // For strips, ALWAYS use the clipped boundary to ensure proper constraints
    const renderBoundary = boundary.clippedBoundary || boundary;

    return {
      boundary,
      renderBoundary,
      centerLng,
      centerLat,
      fitsInField: true,
    };
  }
}

// Create replicate features (treatment areas and plot buffers)
export function createReplicateFeatures(
  config: CreateReplicateParams,
): Feature<Polygon>[] {
  const features: Feature<Polygon>[] = [];

  // Parse and organize treatments
  const orderedTreatments = parseUniqueTreatments(
    config.treatmentRates,
    config.nominalRate,
  );
  if (orderedTreatments.length === 0) {
    return features;
  }

  // Randomize the plot order but keep color-to-rate mapping consistent
  const shuffledTreatments = shuffleArray(orderedTreatments);
  const treatmentColors = generateSummerColors(orderedTreatments.length);

  // Create color mapping: nominal/lowest rate gets brightest yellow (index 0)
  const colorMap = new Map<number, string>();
  orderedTreatments.forEach((rate, index) => {
    colorMap.set(rate, treatmentColors[index]);
  });

  // Calculate plot dimensions
  const plotDimensions = convertPlotDimensions(
    config.plotWidthFt,
    config.plotLengthFt,
  );

  // Create replicate boundary and positioning info
  const boundaryInfo = createReplicateBoundaryInfo(
    config,
    shuffledTreatments,
    plotDimensions,
  );

  // If plot doesn't fit, return empty features
  if (!boundaryInfo.fitsInField) {
    return features;
  }

  // Add the replicate boundary
  boundaryInfo.renderBoundary.properties = { type: 'replicateBoundary' };
  features.push(boundaryInfo.renderBoundary);

  // Create individual treatment areas and buffers
  const treatmentFeatures = createTreatmentFeatures(
    config,
    boundaryInfo,
    shuffledTreatments,
    colorMap,
  );

  features.push(...treatmentFeatures);

  return features;
}

/**
 * Creates individual treatment areas and their buffers
 */
function createTreatmentFeatures(
  config: CreateReplicateParams,
  boundaryInfo: ReplicateBoundaryInfo,
  shuffledTreatments: number[],
  colorMap: Map<number, string>,
): Feature<Polygon>[] {
  const features: Feature<Polygon>[] = [];
  const {
    usableArea,
    isPlot,
    plotWidthFt,
    plotLengthFt,
    plotBufferWidthFt,
    plotBufferLengthFt,
  } = config;

  // Create individual treatment areas by subdividing the replicate boundary
  // Always use the original boundary for subdivision (assumes rectangular shape)
  // Individual treatment areas will be clipped to usable area afterward
  const replicateCorners = boundaryInfo.boundary.geometry
    .coordinates[0] as Position[];
  const cleanCorners = replicateCorners.slice(0, -1);

  for (let i = 0; i < shuffledTreatments.length; i++) {
    const treatmentRate = shuffledTreatments[i];
    const plotColor = colorMap.get(treatmentRate);

    // Calculate this treatment's portion of the replicate (0 to 1)
    const startRatio = i / shuffledTreatments.length;
    const endRatio = (i + 1) / shuffledTreatments.length;

    // Subdivide the replicate boundary for this treatment
    const treatmentCorners = subdivideReplicateBoundary(
      cleanCorners,
      startRatio,
      endRatio,
    );

    // Create treatment area
    let treatmentArea = turf.polygon([treatmentCorners]);
    treatmentArea.properties = {
      type: 'treatmentArea',
      treatmentIndex: i,
      treatmentRate,
      plotColor,
    };

    // For strips, clip each treatment area to the usable area
    if (!isPlot) {
      try {
        const treatmentCollection = turf.featureCollection([
          treatmentArea,
          usableArea,
        ]);
        const clippedTreatment = turf.intersect(treatmentCollection);

        if (clippedTreatment && clippedTreatment.geometry) {
          clippedTreatment.properties = treatmentArea.properties; // Preserve treatment properties
          treatmentArea = clippedTreatment as Feature<Polygon>;
        }
      } catch (err) {
        // Silently use original treatment area if clipping fails
      }
    }

    features.push(treatmentArea);

    // Add plot buffers if specified
    if (isPlot && (plotBufferLengthFt > 0 || plotBufferWidthFt > 0)) {
      // For plots: create both width and length buffers
      const plotBuffers = createPlotBuffers({
        treatmentCorners,
        plotWidthFt,
        plotLengthFt,
        plotBufferWidthFt,
        plotBufferLengthFt,
      });
      features.push(...plotBuffers);
    } else if (!isPlot && plotBufferWidthFt > 0) {
      // For strips: create only width buffers (no length buffers)
      const stripBuffers = createPlotBuffers({
        treatmentCorners,
        plotWidthFt,
        plotLengthFt,
        plotBufferWidthFt,
        plotBufferLengthFt: 0, // No length buffers for strips
      });

      // Clip each strip buffer to the usable area (same as treatment areas)
      const clippedStripBuffers: Feature<Polygon>[] = [];
      for (const buffer of stripBuffers) {
        try {
          const bufferCollection = turf.featureCollection([buffer, usableArea]);
          const clippedBuffer = turf.intersect(bufferCollection);

          if (clippedBuffer && clippedBuffer.geometry) {
            clippedBuffer.properties = buffer.properties; // Preserve buffer properties
            clippedStripBuffers.push(clippedBuffer as Feature<Polygon>);
          }
        } catch (err) {
          // Silently use original buffer if clipping fails
          clippedStripBuffers.push(buffer);
        }
      }

      features.push(...clippedStripBuffers);
    }
  }

  return features;
}

// Create field buffer visualization feature
export function createFieldBufferFeature(
  fieldBoundary: Feature<Polygon | MultiPolygon>,
  usableArea: Feature<Polygon | MultiPolygon>,
): Feature<Polygon | MultiPolygon> | null {
  if (!usableArea || !usableArea.geometry) {
    return null;
  }

  const fieldGeom = fieldBoundary.geometry;
  const usableGeom = usableArea.geometry;

  // For simple polygons, create a donut polygon for the buffer
  if (fieldGeom.type === 'Polygon' && usableGeom.type === 'Polygon') {
    const bufferDonut = turf.polygon([
      fieldGeom.coordinates[0], // Outer ring
      usableGeom.coordinates[0], // Inner ring (the hole)
    ]);
    bufferDonut.properties = { type: 'fieldBuffer' };
    return bufferDonut;
  } else {
    // For complex MultiPolygons, use a fallback visualization
    const fallbackBuffer = turf.clone(usableArea);
    fallbackBuffer.properties = { type: 'fieldBuffer' };
    return fallbackBuffer;
  }
}
